<template>
  <div class="relative">
    <button
      @click="isOpen = !isOpen"
      class="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
      type="button"
    >
      <span class="text-2xl">{{ currentFlag }}</span>
      <span class="text-sm font-medium text-gray-700">{{ currentLangCode }}</span>
      <svg 
        class="w-4 h-4 text-gray-500 transition-transform" 
        :class="{ 'rotate-180': isOpen }"
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    
    <div
      v-if="isOpen"
      class="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
    >
      <button
        v-for="lang in languages"
        :key="lang.code"
        @click="selectLanguage(lang.code)"
        class="w-full flex items-center space-x-3 px-4 py-2 hover:bg-gray-50 transition-colors"
        :class="{ 'bg-primary-50': currentLocale === lang.code }"
        type="button"
      >
        <span class="text-2xl">{{ lang.flag }}</span>
        <span class="text-sm font-medium text-gray-700">{{ lang.name }}</span>
        <svg 
          v-if="currentLocale === lang.code"
          class="w-4 h-4 text-primary-600 ml-auto"
          fill="currentColor" 
          viewBox="0 0 20 20"
        >
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { setLocale, type SupportedLocale } from '@/i18n'

const { locale } = useI18n()
const isOpen = ref(false)

const languages = [
  { code: 'cs' as SupportedLocale, name: 'Čeština', flag: '🇨🇿' },
  { code: 'de' as SupportedLocale, name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en' as SupportedLocale, name: 'English', flag: '🇬🇧' }
]

const currentLocale = computed(() => locale.value as SupportedLocale)

const currentFlag = computed(() => {
  const lang = languages.find(l => l.code === currentLocale.value)
  return lang?.flag || '🇨🇿'
})

const currentLangCode = computed(() => {
  return currentLocale.value.toUpperCase()
})

function selectLanguage(code: SupportedLocale) {
  setLocale(code)
  isOpen.value = false
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement
  if (!target.closest('.relative')) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
